import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TransportProvider } from '@connectrpc/connect-query'

// Initialize Sentry FIRST, before any other initialization
import { initSentry } from './lib/sentry'
initSentry()

// Import the generated route tree
import { routeTree } from './routeTree.gen'
import { connectTransport } from './lib/connect-transport'
import { reportError } from './lib/error-reporting'
import { ErrorBoundary } from './components/ErrorBoundary'

import './styles.css'

// Create a QueryClient instance
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err) => reportError(err, { kind: 'query' }),
  }),
  mutationCache: new MutationCache({
    onError: (err) => reportError(err, { kind: 'mutation' }),
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Render the app
const rootElement = document.getElementById('root')
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <TransportProvider transport={connectTransport}>
          <ErrorBoundary>
            <RouterProvider router={router} />
          </ErrorBoundary>
        </TransportProvider>
      </QueryClientProvider>
    </StrictMode>,
  )
}

